// ui/services/todosSummary.ts
// ✅ 覆蓋全文
//
// Goal
// - Convert TrueTodoItem[] into Dashboard "DailySummaryResult" (三人待辦 + 各模組統計)
// - In offline/demo (未串接 Firebase) 情境：
//   1) 若傳入 trueTodos 為空，會嘗試掃描 localStorage 裡的 DEMO 資料，避免儀錶板整排 0。
//   2) 若仍取不到，最後回退到 offlineDemoPreset()（保底避免全 0）。

import type { TrueTodoItem } from "./trueTodoAdapter";
import type {
  DailySummaryResult,
  PendingGrouped,
  PendingItem,
  PendingModule,
  SummaryCounts,
  SummaryUserKey,
  UserRealtimeSummary,
} from "./dailySummaryService";
import { computeCounts, emptyPendingGrouped } from "./dailySummaryService";

// --- Compatibility exports ---
// Older code paths (useTodosLive / useTodosLiveMonitor / todosService) import these symbols from this module.
// They are kept for backward compatibility, even if the main dashboard now prefers TrueTodo-based aggregation.
export type TodoItem = {
  id: string;
  todoStatus?: string;
  module?: string;
  moduleKey?: string;
  entityId?: string;
  entityNo?: string;
  docNo?: string;
  title?: string;
  overallStatus?: string;
  status?: string;
  createdAt?: any;
  updatedAt?: any;
  currentOwnerName?: string | null;
  currentOwnerUserId?: string | null;
  requesterDept?: string;
  [k: string]: any;
};


const USER_ORDER: SummaryUserKey[] = ["kevin", "ops", "gm"];

const DISPLAY_NAME: Record<SummaryUserKey, string> = {
  kevin: "法務",
  ops: "財務長",
  gm: "總經理",
};

function nowIso(): string {
  return new Date().toISOString();
}

function todayKey(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function todayLabel(): string {
  try {
    return new Date().toLocaleDateString("zh-TW", { year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    return todayKey();
  }
}

function isObj(v: any): v is Record<string, any> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function safeJsonParse(s: string): any | null {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

export function isDemoOfflineMode(): boolean {
  try {
    // eslint-disable-next-line no-undef
    if (typeof window === "undefined") return false;
    const ls = window.localStorage;
    const flag = String(ls.getItem("DEMO_OFFLINE") ?? "").toLowerCase();
    if (flag === "1" || flag === "true" || flag === "yes") return true;

    // Vite env fallback (optional)
    // eslint-disable-next-line no-undef
    const envFlag = (import.meta as any)?.env?.VITE_DEMO_OFFLINE;
    return String(envFlag ?? "") === "1";
  } catch {
    return false;
  }
}

function mapTrueTodoModuleToPendingModule(m: any): PendingModule | null {
  const s = String(m ?? "").toUpperCase();

  if (s.includes("CONTRACT")) return "contractReviews" as PendingModule;
  if (s.includes("TEMPLATE")) return "templates" as PendingModule;
  if (s.includes("SEAL")) return "seals" as PendingModule;
  if (s.includes("CASE") || s.includes("DISPUTE")) return "cases" as PendingModule;
  if (s.includes("GOV") || s.includes("LETTER")) return "govCorrespondence" as PendingModule;
  if (s.includes("COMPLIANCE") || s.includes("INTEL")) return "regIntel" as PendingModule;
  if (s.includes("PROJECT")) return "projectControl" as PendingModule;
  if (s.includes("GOVERNANCE") || s.includes("WORKSPACE") || s.includes("CORE")) return "governanceWorkspace" as PendingModule;

  // brand asset / legacy contract：若你的 PendingModule 有這些 key，可在此加上；否則先忽略
  return null;
}

function emptyCounts(): SummaryCounts {
  // use computeCounts(empty) to align with schema
  const p = emptyPendingGrouped();
  return computeCounts(p);
}

function initUserSummary(userKey: SummaryUserKey): UserRealtimeSummary {
  const pendingList = emptyPendingGrouped();
  const counts = computeCounts(pendingList);
  return {
    userKey,
    displayName: DISPLAY_NAME[userKey],
    counts,
    dateLabel: todayLabel(),
    pendingList,
  };
}

function pushPending(
  user: UserRealtimeSummary,
  moduleKey: PendingModule,
  item: PendingItem
): void {
  const list = user.pendingList as any;
  if (!Array.isArray(list[moduleKey])) list[moduleKey] = [];
  list[moduleKey].push(item);
}

function recomputeUserCounts(user: UserRealtimeSummary): void {
  user.counts = computeCounts(user.pendingList);
}

function buildSummaryFromTrueTodos(trueTodos: TrueTodoItem[], source: string, extraDebug?: any): DailySummaryResult {
  const byUser: Record<SummaryUserKey, UserRealtimeSummary> = {
    kevin: initUserSummary("kevin"),
    ops: initUserSummary("ops"),
    gm: initUserSummary("gm"),
  };

  const unknownModules: Record<string, number> = {};

  for (const t of trueTodos ?? []) {
    const moduleKey = mapTrueTodoModuleToPendingModule((t as any).module);
    if (!moduleKey) {
      const mk = String((t as any).module ?? "UNKNOWN");
      unknownModules[mk] = (unknownModules[mk] ?? 0) + 1;
      continue;
    }

    const targets = Array.isArray((t as any).targetUsers) ? (t as any).targetUsers : [];
    const targetKeys: SummaryUserKey[] = targets
      .map((u: any) => String(u?.userKey ?? "").trim())
      .filter((k: any) => k === "kevin" || k === "ops" || k === "gm") as SummaryUserKey[];

    // 如果 targetUsers 空，保底丟 kevin
    const finalTargets: SummaryUserKey[] = targetKeys.length ? targetKeys : ["kevin"];

    for (const uk of finalTargets) {
      const user = byUser[uk];
      const item: PendingItem = {
        id: String((t as any).id ?? (t as any).key ?? ""),
        title: String((t as any).title ?? "(未命名)"),
        module: moduleKey,
        docNo: (t as any).docNo,
        source: String((t as any).source ?? ""),
        rawId: String((t as any).sourceId ?? ""),
        moduleKey: String((t as any).module ?? ""),
        status: String((t as any)?._debug?.status ?? ""),
        stageKey: String((t as any)?._debug?.currentStageKey ?? ""),
        targetUserKeys: finalTargets,
      };

      pushPending(user, moduleKey, item);
    }
  }

  // recompute counts
  for (const uk of USER_ORDER) recomputeUserCounts(byUser[uk]);

  const result: DailySummaryResult = {
    dateKey: todayKey(),
    dateLabel: todayLabel(),
    generatedAt: nowIso(),
    users: USER_ORDER.map((uk) => byUser[uk]),
    dueSoon: { today: [], within30: [], within60: [] },
    warmTip: undefined,
    debug: {
      schemaVersion: "todosSummary_v20260218",
      raw: {
        source,
        trueTodos: trueTodos?.length ?? 0,
        unknownModules,
        ...extraDebug,
      },
    },
  };

  return result;
}

// --- Offline helper: scan localStorage for demo docs and synthesize minimal "trueTodos" shape ---
function deriveTargetUserKeysFromFlowLike(flowLike: any): SummaryUserKey[] {
  const upper = (v: any) => String(v ?? "").toUpperCase();
  const status = upper(flowLike?.status ?? flowLike?.state ?? flowLike?.approvalStatus);

  // ✅ 整體 NOT_STARTED → 直接算 kevin
  if (status === "NOT_STARTED") return ["kevin"];

  const stages: any[] = Array.isArray(flowLike?.stages)
    ? flowLike.stages
    : Array.isArray(flowLike?.flowStages)
      ? flowLike.flowStages
      : [];

  const currentKey = String(flowLike?.currentStageKey ?? flowLike?.stageKey ?? flowLike?.currentStage ?? "").trim();

  const fromStageKey = (stageKey: string, stageStatus?: any): SummaryUserKey[] => {
    const st = upper(stageStatus);

    // ✅ stage NOT_STARTED → 也直接算 kevin
    if (st === "NOT_STARTED") return ["kevin"];

    const k = upper(stageKey);
    if (!k) return [];

    if (k.includes("LEGAL")) return ["kevin"];
    if (k.includes("CFO") || k.includes("COO") || k.includes("OPS") || k.includes("OPERATION")) return ["ops"];
    if (k.includes("GM") || k.includes("PRES") || k.includes("CEO")) return ["gm"];

    return [];
  };

  if (currentKey) {
    const match = stages.find((s) => String(s?.stageKey ?? s?.key ?? "").trim() === currentKey);
    const keys = fromStageKey(currentKey, match?.status ?? match?.state);
    if (keys.length) return keys;
  }

  const pending = stages.find((s) => {
    const st = upper(s?.status ?? s?.state);
    return st === "PENDING" || st === "IN_PROGRESS" || st === "WAITING";
  });

  if (pending) {
    const keys = fromStageKey(String(pending?.stageKey ?? pending?.key ?? ""), pending?.status ?? pending?.state);
    if (keys.length) return keys;
  }

  return ["kevin"];
}

function tryBuildTrueTodosFromLocalStorage(max = 500): { items: TrueTodoItem[]; debug: any } {
  try {
    // eslint-disable-next-line no-undef
    if (typeof window === "undefined") return { items: [], debug: { noWindow: true } };
    const ls = window.localStorage;
    if (!ls) return { items: [], debug: { noLocalStorage: true } };

    const items: any[] = [];
    const debug: any = { keysScanned: 0, matchedKeys: [] as string[] };

    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      if (!k) continue;

      debug.keysScanned++;
      const kk = k.toLowerCase();
      const maybe =
        kk.includes("approval") ||
        kk.includes("flow") ||
        kk.includes("request") ||
        kk.includes("todo") ||
        kk.includes("contract") ||
        kk.includes("seal") ||
        kk.includes("case") ||
        kk.includes("gov") ||
        kk.includes("compliance") ||
        kk.includes("project");

      if (!maybe) continue;

      const raw = ls.getItem(k);
      if (!raw) continue;

      const parsed = safeJsonParse(raw);
      if (!parsed) continue;

      const pushDoc = (d: any, idHint: string) => {
        if (!d) return;
        // only keep objects
        if (!isObj(d)) return;

        const isFlowLike = Array.isArray((d as any).stages) || Array.isArray((d as any).flowStages);
        const isTodoLike = !!(d as any).module || !!(d as any).todoModule || !!(d as any).category || isFlowLike;
        if (!isTodoLike) return;

        const id = String((d as any).id ?? (d as any)._id ?? (d as any).docId ?? (d as any).requestId ?? (d as any).flowId ?? idHint);

        // Synthesize minimal TrueTodoItem
        const title = String((d as any).title ?? (d as any).name ?? (d as any).subject ?? "(未命名)");
        const module = String((d as any).module ?? (d as any).todoModule ?? (d as any).type ?? (d as any).category ?? "UNKNOWN");

        const targetUserKeys = isFlowLike ? deriveTargetUserKeysFromFlowLike(d) : (["kevin"] as SummaryUserKey[]);

        items.push({
          key: `LS:${id}`,
          id,
          source: "LOCAL_STORAGE",
          sourceId: id,
          module,
          title,
          docNo: String((d as any).docNo ?? (d as any).docNumber ?? (d as any).serial ?? ""),
          createdAtMs: Date.now(),
          updatedAtMs: Date.now(),
          daysOpen: 0,
          urgencyBucket: "GREEN",
          targetUsers: targetUserKeys.map((u) => ({ userKey: u })),
          _debug: {
            status: (d as any).status ?? (d as any).state ?? (d as any).approvalStatus,
            currentStageKey: (d as any).currentStageKey ?? (d as any).stageKey,
          },
        });
      };

      if (Array.isArray(parsed)) {
        parsed.forEach((d, idx) => pushDoc(d, `ls:${k}:${idx}`));
      } else if (isObj(parsed)) {
        // common array props
        const arrKeys = ["items", "docs", "data", "list", "rows", "records", "flows", "requests", "todos"];
        let matched = false;
        for (const ak of arrKeys) {
          if (Array.isArray((parsed as any)[ak])) {
            (parsed as any)[ak].forEach((d: any, idx: number) => pushDoc(d, `ls:${k}:${ak}:${idx}`));
            matched = true;
          }
        }
        if (!matched) {
          // shallow scan
          for (const [subk, subv] of Object.entries(parsed)) {
            if (Array.isArray(subv)) subv.forEach((d: any, idx: number) => pushDoc(d, `ls:${k}:${subk}:${idx}`));
          }
        }
      }

      if (items.length >= max) {
        debug.matchedKeys.push(k);
        break;
      }

      if (items.length) debug.matchedKeys.push(k);
    }

    return { items: items.slice(0, max) as TrueTodoItem[], debug };
  } catch (e: any) {
    return { items: [], debug: { error: String(e?.message ?? e) } };
  }
}

export function offlineDemoPreset(): DailySummaryResult {
  // 保底：避免整個 dashboard 都 0，讓 demo 版至少看得到格式與卡片效果
  const stub: any[] = [
    {
      id: "DEMO-1",
      key: "DEMO:1",
      source: "DEMO",
      sourceId: "DEMO-1",
      module: "CONTRACT_REVIEW",
      title: "（DEMO）合約草案待審",
      targetUsers: [{ userKey: "kevin" }],
    },
    {
      id: "DEMO-2",
      key: "DEMO:2",
      source: "DEMO",
      sourceId: "DEMO-2",
      module: "SEAL",
      title: "（DEMO）用印申請待簽",
      targetUsers: [{ userKey: "ops" }],
    },
    {
      id: "DEMO-3",
      key: "DEMO:3",
      source: "DEMO",
      sourceId: "DEMO-3",
      module: "PROJECT_CONTROL",
      title: "（DEMO）法定專案追蹤",
      targetUsers: [{ userKey: "gm" }],
    },
  ];

  return buildSummaryFromTrueTodos(stub as any, "offline-demo-preset");
}

export function computeTrueDashboardSummary(trueTodos: TrueTodoItem[]): DailySummaryResult {
  const cleaned = Array.isArray(trueTodos) ? trueTodos.filter(Boolean) : [];

  if (cleaned.length > 0) {
    return buildSummaryFromTrueTodos(cleaned, "trueTodos");
  }

  // offline scan (best effort)
  const scan = tryBuildTrueTodosFromLocalStorage(500);
  if (scan.items.length > 0) {
    return buildSummaryFromTrueTodos(scan.items, "localStorage-scan", scan.debug);
  }

  return offlineDemoPreset();
}


function inferUserKeyFromRawTodo(t: TodoItem): SummaryUserKey {
  const name = String(t?.currentOwnerName ?? "");
  const dept = String(t?.requesterDept ?? "");
  const s = (name + " " + dept).toLowerCase();

  if (s.includes("總經理") || s.includes("gm") || s.includes("ceo")) return "gm";
  if (s.includes("財務") || s.includes("營運") || s.includes("ops") || s.includes("coo")) return "ops";
  return "kevin";
}

/**
 * Compatibility: compute summary directly from legacy "todos" (raw todo index docs).
 * Newer dashboard paths should prefer computeTrueDashboardSummary(trueTodos).
 */
export function computeDashboardSummary(openTodos: TodoItem[]): DailySummaryResult {
  const todos = Array.isArray(openTodos) ? openTodos : [];

  const trueTodos: TrueTodoItem[] = todos.map((t, idx) => {
    const module = String((t as any).module || (t as any).moduleKey || "UNKNOWN");
    const entityId = String((t as any).entityId || (t as any).id || `todo_${idx}`);
    const id = String((t as any).id || entityId);
    const uk = inferUserKeyFromRawTodo(t);
    const title = String((t as any).title || "(未命名)");

    return {
      source: "RAW_TODO" as any,
      id,
      key: `RAW_TODO:${module}:${entityId}:${uk}`,
      module,
      title,
      status: String((t as any).overallStatus || (t as any).status || ""),
      docNo: (t as any).entityNo || (t as any).docNo,
      createdAt: (t as any).createdAt,
      updatedAt: (t as any).updatedAt,
      targetUsers: [{ userKey: uk, displayName: DISPLAY_NAME[uk] }],
      raw: t,
    } as any;
  });

  return buildSummaryFromTrueTodos(trueTodos, "rawTodos");
}
